import { c as _c } from "react/compiler-runtime";
import { FormInput, FormSelect, LocaleEnum, Tooltip, getLocaleLanguage, useTranslation as useUiTranslation } from '@systemeio/ui';
import { BLOGS_API, createBlog, removeBlog, updateBlog } from 'modules/blogs/api/blog-api';
import BlogEntityDuplicateModal from 'modules/blogs/components/blog-entity-duplicate-modal';
import TemplateSelector from 'modules/blogs/components/template-selector';
import { BlogLoco } from 'modules/blogs/constants/blog-loco';
import { BlogCreationStatusEnum } from 'modules/blogs/enums/blog-creation-status-enum';
import { useBlogs } from 'modules/blogs/hooks/use-blogs';
import { useDuplicateBlog } from 'modules/blogs/hooks/use-duplicate-blog';
import { BlogEntityDuplicateInterface } from 'modules/blogs/types/blog-entity-interface';
import { BlogCreateInterface, BlogEditInterface, BlogErrorsInterface, BlogInterface } from 'modules/blogs/types/blog-interface';
import { ShareResourceEnum } from 'modules/share/enum/share-resource-enum';
import { getShareUrl } from 'modules/share/utils/get-share-url';
import { NextPage } from 'next';
import React, { useEffect, useState } from 'react';
import { CopyToClipboardButton } from 'shared/components/copy-to-clipboard-button';
import FormUrlPathInput from 'shared/components/form-url-path-input';
import LinkShareModal from 'shared/components/link-share-modal';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useUpdateData } from 'shared/hooks/use-update-data';
import useUser from 'shared/hooks/use-user';
import useUserDomains from 'shared/hooks/use-user-domains';
import { usePagination } from 'shared/route-query/hooks/use-pagination';
import { LocaleDataInterface } from 'shared/types/locale-data-interface';
import { dateTimeFormat } from 'shared/utils/date-time-format';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props';
import { getBlogPostNextjsUrl } from 'shared/utils/get-blog-post-nextjs-url';
import { RssIcon, TimeCircleIcon } from '@systemeio/ui/icons';
const defaultErrors: BlogErrorsInterface = {
  name: '',
  path: '',
  domain: '',
  locale: '',
  template: ''
};
export const getBlogUrl = (domainName: BlogInterface['domain']['domainName'], path: BlogInterface['path']) => `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${domainName}/${path}`;
const Blog: NextPage = () => {
  const $ = _c(160);
  const [shouldFetchDomains, setShouldFetchDomains] = useState(false);
  const {
    userDomains,
    userDomainsSelect,
    userDefaultDomain
  } = useUserDomains(shouldFetchDomains);
  const {
    setPagination,
    pagination,
    setLimit,
    limit
  } = usePagination();
  const [errors, setErrors] = useState(defaultErrors);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      name: "",
      path: "",
      domain: undefined,
      template: undefined
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [defaultValues, setDefaultValues] = useState(t0);
  let t1;
  if ($[1] !== limit || $[2] !== pagination) {
    t1 = {
      limit,
      pagination
    };
    $[1] = limit;
    $[2] = pagination;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    blogs,
    mutate,
    counter
  } = useBlogs(t1);
  const {
    duplicateBlog
  } = useDuplicateBlog();
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [`${BLOGS_API}/templates`];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== setPagination) {
    t3 = {
      setPagination,
      api: BLOGS_API,
      constantApi: t2
    };
    $[5] = setPagination;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const updateData = useUpdateData(t3);
  const {
    t
  } = useLocoTranslation();
  const {
    t: tUi
  } = useUiTranslation();
  const {
    user
  } = useUser();
  let t4;
  if ($[7] !== tUi) {
    t4 = Object.values(LocaleEnum).map(locale => ({
      id: locale as LocaleEnum,
      caption: getLocaleLanguage(locale, tUi)
    }));
    $[7] = tUi;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const localeData = t4;
  let t5;
  if ($[9] !== userDefaultDomain) {
    t5 = () => {
      if (userDefaultDomain) {
        setDefaultValues(prev => ({
          ...prev,
          domain: userDefaultDomain.id
        }));
      }
    };
    $[9] = userDefaultDomain;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== userDefaultDomain || $[12] !== userDomains) {
    t6 = [userDomains, userDefaultDomain];
    $[11] = userDefaultDomain;
    $[12] = userDomains;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== blogs || $[15] !== duplicateBlog || $[16] !== updateData) {
    t7 = async (id, blog) => {
      ;
      try {
        await duplicateBlog(id, blog);
        await updateData(false, blogs);
      } catch (t8) {
        const e = t8;
        throw e;
      }
    };
    $[14] = blogs;
    $[15] = duplicateBlog;
    $[16] = updateData;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const duplicateHandler = t7;
  let t8;
  if ($[18] !== blogs || $[19] !== updateData) {
    t8 = async blog_0 => {
      await removeBlog(blog_0.id);
      await updateData(false, blogs);
    };
    $[18] = blogs;
    $[19] = updateData;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const onRemoveBlog = t8;
  let t9;
  if ($[21] !== blogs || $[22] !== mutate || $[23] !== updateData) {
    t9 = async (blog_1, data) => {
      await fetchWithErrors(async () => {
        const newData = {
          name: data.name,
          domain: data.domain.id,
          path: data.path,
          locale: data.locale
        };
        const response = await updateBlog(blog_1.id, newData);
        await updateData(false, undefined, {
          mutationData: blogs,
          onMutate: async () => {
            await mutate(data_0 => {
              if (data_0) {
                return {
                  items: data_0.items.map(item => {
                    if (item.id === blog_1.id) {
                      item = response.data;
                    }
                    return item;
                  }),
                  hasMore: data_0.hasMore
                };
              }
            }, false);
          }
        });
      }, setErrors);
    };
    $[21] = blogs;
    $[22] = mutate;
    $[23] = updateData;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const onEditBlog = t9;
  let t10;
  if ($[25] !== blogs || $[26] !== updateData) {
    t10 = async data_1 => {
      await fetchWithErrors(async () => {
        const newDate = {
          template: data_1.template,
          path: data_1.path || "",
          domain: data_1.domain,
          name: data_1.name
        };
        await createBlog(newDate);
        await updateData(true, blogs);
      }, setErrors);
    };
    $[25] = blogs;
    $[26] = updateData;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const onCreateBlog = t10;
  let t11;
  if ($[28] !== t) {
    t11 = t("global.title");
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = {
      key: "name",
      header: t11,
      widthPercent: 35,
      onRender: _temp
    };
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t) {
    t13 = t("dashboard.blog.home_page_url");
    $[32] = t;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t13) {
    t14 = {
      key: "domain",
      header: t13,
      onRender: _temp2,
      widthPercent: 45
    };
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t) {
    t15 = t("global.created_on");
    $[36] = t;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t || $[39] !== user?.dateFormat || $[40] !== user?.timeFormat || $[41] !== user?.timezone) {
    t16 = blog_4 => <div className="flex gap-2"><span>{dateTimeFormat(blog_4.createdAt, {
          timeFormat: user?.timeFormat,
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })}</span>{blog_4.creationStatus !== BlogCreationStatusEnum.Completed && <Tooltip label={t("dashboard.blog.home_page.tooltip_creation_status")} mode="hover" key="status-tooltip"><TimeCircleIcon className="fill-yellow h-[16px] w-[16px] sm:h-[20px] sm:w-[20px]" /></Tooltip>}</div>;
    $[38] = t;
    $[39] = user?.dateFormat;
    $[40] = user?.timeFormat;
    $[41] = user?.timezone;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t15 || $[44] !== t16) {
    t17 = {
      key: "createdAt",
      header: t15,
      onRender: t16,
      widthPercent: 20
    };
    $[43] = t15;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t12 || $[47] !== t14 || $[48] !== t17) {
    t18 = [t12, t14, t17];
    $[46] = t12;
    $[47] = t14;
    $[48] = t17;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  const columns = t18;
  let t19;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setErrors(defaultErrors);
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const onModalClose = t19;
  let t20;
  if ($[51] !== t) {
    t20 = t("dashboard.blog.title_plural");
    $[51] = t;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = async () => setShouldFetchDomains(true);
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] !== onCreateBlog) {
    t22 = data_2 => onCreateBlog(data_2);
    $[54] = onCreateBlog;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t("dashboard.blog.title");
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const t24 = !blogs;
  let t25;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = ["name", "domain", "template"];
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== errors || $[60] !== t) {
    t26 = {
      field: "name",
      onRender: (value, onChange) => <FormInput required={true} key="blog-name-create" label={t("global.name")} value={value} onChange={e_0 => onChange(e_0.target.value)} error={errors.name} />
    };
    $[59] = errors;
    $[60] = t;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== errors || $[63] !== t || $[64] !== userDomainsSelect) {
    t27 = {
      field: "domain",
      onRender: (value_0, onChange_0) => <FormSelect required={true} key="blog-domain-create" onChange={value_1 => onChange_0(value_1)} value={value_0} label={t("dashboard.blog.domain_name")} data={userDomainsSelect} error={errors.domain} />
    };
    $[62] = errors;
    $[63] = t;
    $[64] = userDomainsSelect;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t26 || $[67] !== t27) {
    t28 = [t26, t27];
    $[66] = t26;
    $[67] = t27;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  let t29;
  if ($[69] !== errors || $[70] !== t || $[71] !== userDomainsSelect) {
    t29 = {
      field: "path",
      onRender: (value_2, onChange_1, data_3) => <FormUrlPathInput key="blog-path-create" error={errors.path} value={value_2} onChange={e_1 => onChange_1(e_1.target.value)} label={t("global.url_path")} domainName={userDomainsSelect?.find(el => el.id === data_3.domain)?.caption} />
    };
    $[69] = errors;
    $[70] = t;
    $[71] = userDomainsSelect;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      field: "template",
      onRender: _temp3
    };
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== t28 || $[75] !== t29) {
    t31 = [t28, t29, t30];
    $[74] = t28;
    $[75] = t29;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== defaultValues || $[78] !== t22 || $[79] !== t23 || $[80] !== t24 || $[81] !== t31) {
    t32 = {
      onOpen: t21,
      defaultValues,
      onCreate: t22,
      onAfterLeave: onModalClose,
      caption: t23,
      disabled: t24,
      requiredFields: t25,
      creatable: t31
    };
    $[77] = defaultValues;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] !== t) {
    t33 = t("dashboard.blog.title");
    $[83] = t;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  let t34;
  if ($[85] !== limit || $[86] !== setLimit) {
    t34 = {
      limit,
      setLimit
    };
    $[85] = limit;
    $[86] = setLimit;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== pagination || $[89] !== setPagination) {
    t35 = {
      pagination,
      setPagination
    };
    $[88] = pagination;
    $[89] = setPagination;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = [{
      name: "view-blog",
      setHref: _temp4,
      openInNew: true,
      shouldRender: _temp5
    }];
    $[91] = t36;
  } else {
    t36 = $[91];
  }
  let t37;
  if ($[92] !== t) {
    t37 = t("dashboard.blog.edit_blog");
    $[92] = t;
    $[93] = t37;
  } else {
    t37 = $[93];
  }
  let t38;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setShouldFetchDomains(true);
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] !== onEditBlog) {
    t39 = (blog_7, data_4) => onEditBlog(blog_7, data_4);
    $[95] = onEditBlog;
    $[96] = t39;
  } else {
    t39 = $[96];
  }
  let t40;
  if ($[97] !== errors || $[98] !== t) {
    t40 = {
      field: "name",
      onRender: (value_4, onChange_3) => <FormInput key="blog-name-edit" value={value_4} onChange={e_2 => onChange_3(e_2.target.value)} error={errors.name} label={t("global.name")} />
    };
    $[97] = errors;
    $[98] = t;
    $[99] = t40;
  } else {
    t40 = $[99];
  }
  let t41;
  if ($[100] !== errors || $[101] !== t || $[102] !== userDomainsSelect) {
    t41 = {
      field: "domain",
      onRender: (value_5, onChange_4) => <FormSelect isPreFetching={!userDomainsSelect} key="blog-domain-edit" onChange={value_6 => value_6 && onChange_4({
        id: value_6,
        domainName: ""
      })} value={value_5.id} withoutCloseIcon={true} label={t("dashboard.blog.domain_name")} data={userDomainsSelect} error={errors.domain} />
    };
    $[100] = errors;
    $[101] = t;
    $[102] = userDomainsSelect;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  if ($[104] !== t40 || $[105] !== t41) {
    t42 = [t40, t41];
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== errors || $[108] !== t || $[109] !== userDomainsSelect) {
    t43 = {
      field: "path",
      onRender: (value_7, onChange_5, data_5) => {
        const domainName = userDomainsSelect?.find(el_0 => {
          const domain = data_5.domain;
          return el_0.id === domain.id;
        })?.caption;
        return <div key="blog-path-edit-and-rss-feed" className="flex flex-col gap-5"><FormUrlPathInput label={t("global.url_path")} error={errors.path} value={value_7} onChange={e_3 => onChange_5(e_3.target.value)} domainName={domainName} /><FormUrlPathInput label={<div className="flex items-end gap-1.5">{t("dashboard.blog.rss_feed")}<RssIcon /></div>} domainName={domainName} value={`${data_5.path}${!!data_5.path.length ? "/" : ""}feed`} disabled={true} /></div>;
      }
    };
    $[107] = errors;
    $[108] = t;
    $[109] = userDomainsSelect;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== errors || $[112] !== localeData || $[113] !== t) {
    t44 = {
      field: "locale",
      onRender: (value_8, onChange_6) => <FormSelect key="blog-locale-edit" onChange={value_9 => value_9 && onChange_6(value_9)} value={value_8} withoutCloseIcon={true} label={t("global.language")} data={localeData} placeholder={t("dashboard.blog.choose_domain")} error={errors.locale} />
    };
    $[111] = errors;
    $[112] = localeData;
    $[113] = t;
    $[114] = t44;
  } else {
    t44 = $[114];
  }
  let t45;
  if ($[115] !== t42 || $[116] !== t43 || $[117] !== t44) {
    t45 = [t42, t43, t44];
    $[115] = t42;
    $[116] = t43;
    $[117] = t44;
    $[118] = t45;
  } else {
    t45 = $[118];
  }
  let t46;
  if ($[119] !== t37 || $[120] !== t39 || $[121] !== t45) {
    t46 = {
      fullCaption: t37,
      onModalOpen: t38,
      onModalClose,
      onEdit: t39,
      shouldRender: _temp6,
      editable: t45
    };
    $[119] = t37;
    $[120] = t39;
    $[121] = t45;
    $[122] = t46;
  } else {
    t46 = $[122];
  }
  let t47;
  if ($[123] !== t) {
    t47 = t("dashboard.actions.title.duplicate");
    $[123] = t;
    $[124] = t47;
  } else {
    t47 = $[124];
  }
  let t48;
  if ($[125] !== duplicateHandler || $[126] !== t) {
    t48 = (opened, onClose, blog_9, key) => <BlogEntityDuplicateModal key={key} opened={opened} blogEntity={blog_9} title={t("dashboard.blog.duplicate_blog")} onClose={onClose} onUpdateData={async () => {
      onModalClose();
      onClose();
    }} duplicateHandler={duplicateHandler} />;
    $[125] = duplicateHandler;
    $[126] = t;
    $[127] = t48;
  } else {
    t48 = $[127];
  }
  let t49;
  if ($[128] !== t47 || $[129] !== t48) {
    t49 = {
      name: "duplicate",
      caption: t47,
      onActionRender: t48,
      shouldRender: _temp7
    };
    $[128] = t47;
    $[129] = t48;
    $[130] = t49;
  } else {
    t49 = $[130];
  }
  let t50;
  if ($[131] !== t) {
    t50 = t("dashboard.actions.title.share");
    $[131] = t;
    $[132] = t50;
  } else {
    t50 = $[132];
  }
  let t51;
  if ($[133] !== t) {
    t51 = (opened_0, onClose_0, blog_11, key_0) => <LinkShareModal opened={opened_0} link={getShareUrl(ShareResourceEnum.Blog, blog_11.hash)} title={t("dashboard.blog.share_blog")} label={t("dashboard.blog.share_blog_text")} onClose={onClose_0} key={key_0} />;
    $[133] = t;
    $[134] = t51;
  } else {
    t51 = $[134];
  }
  let t52;
  if ($[135] !== t50 || $[136] !== t51) {
    t52 = {
      name: "share",
      caption: t50,
      onActionRender: t51,
      shouldRender: _temp8
    };
    $[135] = t50;
    $[136] = t51;
    $[137] = t52;
  } else {
    t52 = $[137];
  }
  let t53;
  if ($[138] !== t49 || $[139] !== t52) {
    t53 = [t49, t52];
    $[138] = t49;
    $[139] = t52;
    $[140] = t53;
  } else {
    t53 = $[140];
  }
  let t54;
  if ($[141] !== onRemoveBlog) {
    t54 = {
      onChange: blog_13 => onRemoveBlog(blog_13),
      shouldRender: _temp9
    };
    $[141] = onRemoveBlog;
    $[142] = t54;
  } else {
    t54 = $[142];
  }
  let t55;
  if ($[143] !== t46 || $[144] !== t53 || $[145] !== t54) {
    t55 = {
      openLink: t36,
      settings: t46,
      custom: t53,
      remove: t54
    };
    $[143] = t46;
    $[144] = t53;
    $[145] = t54;
    $[146] = t55;
  } else {
    t55 = $[146];
  }
  let t56;
  if ($[147] !== blogs || $[148] !== columns || $[149] !== t33 || $[150] !== t34 || $[151] !== t35 || $[152] !== t55) {
    t56 = <div data-test="blog-list"><Table caption={t33} data={blogs} columns={columns} limiter={t34} paginator={t35} actions={t55} /></div>;
    $[147] = blogs;
    $[148] = columns;
    $[149] = t33;
    $[150] = t34;
    $[151] = t35;
    $[152] = t55;
    $[153] = t56;
  } else {
    t56 = $[153];
  }
  let t57;
  if ($[154] !== blogs || $[155] !== counter || $[156] !== t20 || $[157] !== t32 || $[158] !== t56) {
    t57 = <ListingLayout counter={counter} data={blogs} title={t20} type="compact" createData={t32}>{t56}</ListingLayout>;
    $[154] = blogs;
    $[155] = counter;
    $[156] = t20;
    $[157] = t32;
    $[158] = t56;
    $[159] = t57;
  } else {
    t57 = $[159];
  }
  return t57;
};
export const getServerSideProps = getBaseServerSideProps(BlogLoco, 'header.top_menu.blogs');
export default Blog;
function _temp(blog_2) {
  return <div className="truncate">{blog_2.creationStatus === BlogCreationStatusEnum.Completed ? <LinkWithoutPrefetch className="primary-link w-full" href={getBlogPostNextjsUrl(blog_2.id)}>{blog_2.name}</LinkWithoutPrefetch> : <span>{blog_2.name}</span>}</div>;
}
function _temp2(blog_3) {
  const blogUrl = getBlogUrl(blog_3.domain.domainName, blog_3.path);
  return blog_3.creationStatus === BlogCreationStatusEnum.Completed ? <div className="flex w-full gap-3"><div className="truncate">{blogUrl}</div><CopyToClipboardButton text={blogUrl} /></div> : "";
}
function _temp3(value_3, onChange_2) {
  return <TemplateSelector key="blog-template-create" value={value_3} onChange={onChange_2} />;
}
function _temp4(blog_5) {
  return getBlogUrl(blog_5.domain.domainName, blog_5.path);
}
function _temp5(blog_6) {
  return blog_6.creationStatus === BlogCreationStatusEnum.Completed;
}
function _temp6(blog_8) {
  return blog_8.creationStatus === BlogCreationStatusEnum.Completed;
}
function _temp7(blog_10) {
  return blog_10.creationStatus === BlogCreationStatusEnum.Completed;
}
function _temp8(blog_12) {
  return blog_12.creationStatus === BlogCreationStatusEnum.Completed;
}
function _temp9(blog_14) {
  return blog_14.creationStatus === BlogCreationStatusEnum.Completed;
}